import React, { useState, useMemo, useEffect, useRef } from "react";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import { useGetTenants } from "../../../api/tenant/service/index";
import { useNavigate } from "react-router-dom";
import { formatDateForDisplay } from "../../../utils/dateUtils";
import { useTranslation } from "react-i18next";
import { useGetAllSAInventories } from "api/super-admin/service";
import classNames from "classnames";
import SearchInventoryFilters from "../../inventory/components/SearchInventoryFilters";
import SearchInventoryCards from "../../inventory/components/SearchInventoryCards";

const defaultStatuses = [
  { status_name: "Available", status_key: "available", color: "#16a34a" },
  { status_name: "Check-In", status_key: "check-in", color: "#0ea5e9" },
  { status_name: "Deleted", status_key: "deleted", color: "#ef4444" },
  { status_name: "Evaluating", status_key: "evaluating", color: "#eab308" },
  { status_name: "In Transit", status_key: "in-transit", color: "#3b82f6" },
  { status_name: "Not For Sale", status_key: "not-for-sale", color: "#6b7280" },
  { status_name: "On Hold", status_key: "on-hold", color: "#f97316" },
  { status_name: "Pending Sale", status_key: "pending-sale", color: "#a855f7" },
  { status_name: "Recon / Detail", status_key: "recon-detail", color: "#14b8a6" },
  { status_name: "Repair / Service", status_key: "repair-service", color: "#f59e0b" },
  { status_name: "Sold", status_key: "sold", color: "#059669" },
  { status_name: "Spot Delivered", status_key: "spot-delivered", color: "#7c3aed" },
];

const statusOptions = defaultStatuses.map((s) => ({
  label: s.status_name,
  value: s.status_key,
  color: s.color,
}));

const inventoryColumns = (t) => [
  { Header: t("searchInventory.tenant"), accessor: "tenant" },
  { Header: t("searchInventory.stock"), accessor: "stock" },
  { Header: t("searchInventory.year"), accessor: "year", width: 60, minWidth: 60, maxWidth: 60 },
  { Header: t("searchInventory.make"), accessor: "make", width: 100, minWidth: 100, maxWidth: 100 },
  { Header: t("searchInventory.modelTrim"), accessor: "model" },
  { Header: t("searchInventory.color"), accessor: "color" },
  { Header: t("searchInventory.vin"), accessor: "vin" },
  { Header: t("searchInventory.location"), accessor: "location" },
  { Header: t("searchInventory.daysAtLocation"), accessor: "daysAtLocation" },
  { Header: t("searchInventory.acquiredDate"), accessor: "acquiredDate" },
  {
    Header: () => (
      <div style={{ textAlign: "center", width: "100%" }}>
        {t("searchInventory.status")}
      </div>
    ),
    accessor: "status",
    Cell: ({ value }) => <div style={{ textAlign: "center" }}>{value}</div>,
  },
  { Header: t("searchInventory.price"), accessor: "price" },
  { Header: t("searchInventory.mileage"), accessor: "mileage" },
  { Header: t("searchInventory.pics"), accessor: "pics" },
];

const Inventories = ({ activeColor = "blue", shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState([]);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [tenant, setTenant] = useState("");

  const { data: tenantsData, isLoading: isLoadingTenants } = useGetTenants(1, 1000);

  const statusArray = useMemo(
    () => status.map((option) => option.value),
    [status]
  );

  const initializedRef = useRef(false);

  useEffect(() => {
    if (!initializedRef.current && statusOptions.length > 0) {
      const defaultOption = statusOptions.find((opt) => opt.value === "available");
      if (defaultOption) setStatus([defaultOption]);
      initializedRef.current = true;
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handler);
  }, [search]);

  const { data: inventories, isLoading } = useGetAllSAInventories(
    debouncedSearch,
    statusArray,
    "",
    "",
    page,
    pageSize,
    undefined,
    tenant
  );

  const serverPageCount = inventories?.pagination?.pageCount;
  useEffect(() => {
    if (serverPageCount == null || serverPageCount < 1) return;
    if (page > serverPageCount) {
      setPage(serverPageCount);
    }
  }, [serverPageCount, page]);

  useEffect(() => {
    if (isLoading || !inventories?.pagination) return;
    const total = inventories.pagination.total ?? 0;
    const resultsLen = (inventories?.data || inventories?.results || []).length;
    if (total > 0 && resultsLen === 0 && page > 1) {
      setPage(1);
    }
  }, [isLoading, inventories?.pagination?.total, page, inventories?.data, inventories?.results]);

  const columns = useMemo(() => inventoryColumns(t), [t]);

  const inventoryData = useMemo(() => {
    const list = inventories?.data || inventories?.results || [];
    return list.map((item) => ({
      id: item.id,
      documentId: item.documentId,
      tenant: item.tenant?.name || "-",
      stock: item.stock_number,
      year: item.inventory_vehicle?.year || "",
      make: item.inventory_vehicle?.make
        ? item.inventory_vehicle.make.charAt(0).toUpperCase() +
          item.inventory_vehicle.make.slice(1).toLowerCase()
        : "",
      model: item.inventory_vehicle?.model || "",
      color: item.inventory_vehicle?.exterior_color || "",
      vin: item?.vin || "",
      location: item.location?.name || item.location?.legal_name || "-",
      daysAtLocation: item.days_at_location == null ? "-" : item.days_at_location,
      acquiredDate: formatDateForDisplay(item.acquired_date),
      status: (
        <span style={{ color: item.inventory_vehicle?.vehicle_status?.color }}>
          {item.inventory_vehicle?.vehicle_status?.status_name || "-"}
        </span>
      ),
      price: new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(item.inventory_pricing?.normal_asking_price || 0),
      mileage: item.mileage || "",
      pics: item.images_count || 0,
    }));
  }, [inventories]);

  const tenantOptions = useMemo(
    () =>
      (tenantsData?.data || tenantsData?.results || []).map((row) => ({
        value: String(row.code),
        label: row.name,
      })) || [],
    [tenantsData]
  );

  const tenantValue = useMemo(
    () =>
      tenant
        ? {
            value: tenant,
            label:
              tenantOptions.find((opt) => String(opt.value) === String(tenant))?.label || tenant,
          }
        : null,
    [tenant, tenantOptions]
  );

  const isTheme2 = shellThemeVariant === "2";
  const InventoryTable = isTheme2 ? ReactTable2 : ReactTable;

  const filters = (
    <SearchInventoryFilters
      activeColor={activeColor}
      isTheme2={isTheme2}
      search={search}
      onSearchChange={setSearch}
      status={status}
      onStatusChange={setStatus}
      location=""
      onLocationChange={() => {}}
      altLocation=""
      onAltLocationChange={() => {}}
      statusOptions={statusOptions}
      showLocationFilters={false}
      showTenantFilter
      onTenantChange={setTenant}
      tenantValue={tenantValue}
      tenantOptions={tenantOptions}
      isLoadingTenants={isLoadingTenants}
      tenantLabel={t("searchInventory.tenant")}
    />
  );

  const table = (
    <div
      className="inventory-table-wrap"
      style={{ fontSize: "13px", minWidth: 0, maxWidth: "100%" }}
    >
      <InventoryTable
        activeColor={activeColor}
        data={inventoryData}
        columns={columns}
        className={classNames("-striped -highlight", { "sticky-stock-table": isTheme2 })}
        manualPagination={true}
        pageCount={inventories?.pagination?.pageCount || 1}
        pageIndex={page - 1}
        totalItems={inventories?.pagination?.total}
        noDataText={t("searchInventory.noRecordsFound")}
        loading={isLoading}
        loadingText={t("searchInventory.loadingSearchInventory")}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        pageSize={pageSize}
        onPageChange={(pageIndex) => setPage(pageIndex + 1)}
        onRowClick={(row) => {
          if (row?.documentId) {
            navigate(`/inventory/${row.documentId}`);
          }
        }}
      />
    </div>
  );

  return (
    <div
      className={classNames("content search-inventory-page search-inventory-page--sa", {
        "search-inventory-page--theme2": isTheme2,
        "theme2-admin-content-compact": isTheme2,
        "theme2-inventory-search-inset": isTheme2,
      })}
    >
      <SearchInventoryCards
        isTheme2={isTheme2}
        filters={filters}
        table={table}
        title={`Super Admin - ${t("searchInventory.title")}`}
      />
    </div>
  );
};

export default Inventories;
